import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { AuthProvider, useAuth } from './context/AuthContext';
import { auth } from './firebase/firebase';

import Signup from "./components/Signup";
import Login from "./components/Login";
import Dashboard from "./components/Dashboard";
import Settings from "./components/Settings";
import CreateCampaign from "./components/CreateCampaign";
import Analytics from "./components/Analytics";
import Help from "./components/Help";
import LandingPage from "./components/LandingPage";
import ChatLandingPage from "./components/ChatLandingPage"; // ✅ NEW: ChatGPT landing page
import PrivacyPolicy from "./components/PrivacyPolicy";
import TermsOfService from "./components/TermsOfService";
import PricingPage from "./components/PricingPage";
import Profile from "./components/Profile";
import Home from "./components/Home";
import CampaignSuccess from "./components/pages/CampaignSuccess";
import CampaignCancelled from "./components/pages/CampaignCancelled";
import ManageAccounts from "./components/ManageAccounts";
import AuthCallback from "./components/AuthCallback";
import AuthCallbackV2 from "./components/AuthCallbackV2";
import Tasks from "./components/Tasks"; // ✅ FIX 6: Tasks page
import SavebackDesk from "./components/SavebackDesk";

// ErrorBoundary component to catch React errors
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h2>Something went wrong.</h2>
          <details style={{ whiteSpace: 'pre-wrap' }}>
            {this.state.error && this.state.error.toString()}
          </details>
        </div>
      );
    }

    return this.props.children;
  }
}

// Layout for Protected Routes (no Sidebar - each page renders its own)
const ProtectedLayout = ({ children }) => {
  return (
    <div id="root">
      <div className="main-content">
        {children}
        {/* Footer removed to match MEO */}
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user } = useAuth();
  
  console.log("🛡️ ProtectedRoute: Checking authentication");
  console.log("🔍 ProtectedRoute: User from context:", user?.email || "null");
  console.log("🔍 ProtectedRoute: User UID:", user?.uid || "N/A");
  console.log("🔍 ProtectedRoute: auth.currentUser:", auth.currentUser?.email || "null");
  console.log("🔍 ProtectedRoute: localStorage authToken:", localStorage.getItem("authToken") ? "exists" : "missing");

  if (!user) {
    console.warn("⛔ ProtectedRoute: No user found, redirecting to /login");
    return <Navigate to="/login" replace />;
  }

  console.log("✅ ProtectedRoute: User authenticated, rendering protected content");
  return <ProtectedLayout>{children}</ProtectedLayout>;
};

const InactiveIntegrationPage = ({ integration }) => (
  <main className="saveback-desk">
    <section className="saveback-toolbar">
      <div>
        <p className="saveback-kicker">Churn Signal Saveback Desk</p>
        <h1>{integration} inactive for this product</h1>
        <p>
          This frozen run uses run-scoped manual entitlement, Firestore business truth,
          and Redis/BullMQ execution. {integration} architecture is preserved but not enabled.
        </p>
      </div>
    </section>
  </main>
);

// App Routes
const AppRoutes = () => {
  return (
    <Router>
      <Routes>

        {/* Public Routes */}
        <Route path="/" element={<SavebackDesk />} />
        <Route path="/campaign-assistant" element={<ChatLandingPage />} /> {/* ✅ preserved AFC landing page */}
        <Route path="/old-landing" element={<LandingPage />} /> {/* ✅ NEW: Original landing page preserved */}
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/campaign-success" element={<CampaignSuccess />} />
        <Route path="/campaign-cancelled" element={<CampaignCancelled />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/auth/callback-v2" element={<AuthCallbackV2 />} />
        <Route path="/payment/:campaignId" element={<InactiveIntegrationPage integration="Stripe" />} />
        <Route path="/payment" element={<InactiveIntegrationPage integration="Stripe" />} />
        <Route path="/saveback-desk" element={<SavebackDesk />} />

        {/* Protected Routes */}
        <Route path="/home" element={<ProtectedRoute><Home /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="/campaigns" element={<ProtectedRoute><CreateCampaign /></ProtectedRoute>} />
        <Route path="/manage-accounts" element={<ProtectedRoute><ManageAccounts /></ProtectedRoute>} />
        <Route path="/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
        <Route path="/tasks" element={<ProtectedRoute><Tasks /></ProtectedRoute>} /> {/* ✅ FIX 6: Tasks route */}
        <Route path="/subscribe" element={<ProtectedRoute><InactiveIntegrationPage integration="Stripe subscription" /></ProtectedRoute>} />
        <Route path="/help" element={<ProtectedRoute><Help /></ProtectedRoute>} />

      </Routes>
    </Router>
  );
};

// App Root
function App() {
  return (
    <AuthProvider>
      <ErrorBoundary>
        <AppRoutes />
      </ErrorBoundary>
    </AuthProvider>
  );
}

export default App;
